import { toLonLat } from 'ol/proj';

import MapCore from './core/MapCore';
import MapUtils from './core/MapUtils';
import DockFeature from './features/DockFeature';
import FenceFeature from './features/FenceFeature';
import GotoFeature from './features/GotoFeature';
import HomeFeature from './features/HomeFeature';
import PhotoFeature from './features/PhotoFeature';
import PilotFeature from './features/PilotFeature';
import RobotFeature from './features/RobotFeature';
import ShootFeature from './features/ShootFeature';
import TraceFeature from './features/TraceFeature';
import WayFeature from './features/WayFeature';

import { OL_DEFAULT_MAP_ID } from '@/config';

/**
 * OpenLayers 맵 통합 관리 클래스
 * 여러 Feature 모듈들을 통합하여 하나의 인터페이스로 제공
 */
class OlMap {
  constructor() {
    // Core
    this.mapCore = new MapCore();

    // Features (will be initialized after map is created)
    this.dockFeature = null;
    this.fenceFeature = null;
    this.gotoFeature = null;
    this.homeFeature = null;
    this.photoFeature = null;
    this.pilotFeature = null;
    this.robotFeature = null;
    this.shootFeature = null;
    this.traceFeature = null;
    this.wayFeature = null;
  }

  /**
   * 맵 초기화
   */
  init(target, options) {
    const map = this.mapCore.init(target, options, OL_DEFAULT_MAP_ID);

    // Feature 인스턴스 초기화
    const createOverlay = this.mapCore.createOverlay.bind(this.mapCore);
    this.dockFeature = new DockFeature(createOverlay, map);
    this.fenceFeature = new FenceFeature(map);
    this.gotoFeature = new GotoFeature(createOverlay, map);
    this.homeFeature = new HomeFeature(createOverlay, map);
    this.photoFeature = new PhotoFeature(createOverlay, map);
    this.pilotFeature = new PilotFeature(createOverlay, map);
    this.robotFeature = new RobotFeature(createOverlay, map);
    this.shootFeature = new ShootFeature(map);
    this.traceFeature = new TraceFeature(map);
    this.wayFeature = new WayFeature(createOverlay, map);

    return map;
  }

  /**
   * 맵 인스턴스 가져오기
   */
  getMap() {
    return this.mapCore.getMap();
  }

  /**
   * 맵 변경
   */
  changeMap(mapId, typeIndex) {
    this.mapCore.changeMap(mapId, typeIndex);
  }

  /**
   * 맵 중심 설정
   */
  setCenter(position, options) {
    MapUtils.setCenter(this.mapCore.getMap(), position, options);
  }

  /**
   * 맵 중심 가져오기
   */
  getCenter() {
    return MapUtils.getCenter(this.mapCore.getMap());
  }

  /**
   * Bounds 피팅
   */
  fitBounds(positions) {
    MapUtils.fitBounds(this.mapCore.getMap(), positions);
  }

  /**
   * 자동 Bounds 피팅 (로봇 + 미션)
   */
  fitBoundsAuto() {
    // 로봇
    const robots = Object.values(this.robotFeature.getAll()).map((robot) => toLonLat(robot.values_.position));
    // 미션
    const ways = Object.values(this.wayFeature.getAll()).flatMap((way) =>
      way.markers.map((marker) => toLonLat(marker.values_.position))
    );
    let positions = [...robots, ...ways];

    // 현재 지도 VWorld 인 경우
    if (this.checkCurrentVWorld()) {
      // VWorld 영역 내 위치
      positions = positions.filter((position) => this.checkInsideOfVWorld(position));
    }
    this.fitBounds(positions);
  }

  /**
   * 오버레이 생성
   */
  createOverlay(options) {
    return this.mapCore.createOverlay(options);
  }

  /**
   * 색상 변경 (Home, Pilot, Goto)
   */
  changeColor(robotId, color) {
    // Home marker
    const home = this.homeFeature.get(robotId);
    if (home) {
      const position = home.position;
      this.homeFeature.remove(robotId);
      this.homeFeature.addMarker(robotId, position, color);
    }

    // Pilot marker
    const pilot = this.pilotFeature.get(robotId);
    if (pilot) {
      const position = pilot.position;
      this.pilotFeature.removeMarker(robotId);
      this.pilotFeature.addMarker(robotId, position, color);
    }

    // Goto marker and path
    const goto = this.gotoFeature.get(robotId);
    if (goto) {
      const position = goto.position;
      this.gotoFeature.remove(robotId);
      this.gotoFeature.addMarker(robotId, position, color);
      this.gotoFeature.addPath(robotId, position, color);
    }
  }

  //#region VWorld 관련 메서드
  checkCurrentVWorld() {
    return MapUtils.isVWorldMap(this.mapCore.getCurrentMapId());
  }

  checkInsideOfVWorld(position) {
    return MapUtils.isInsideVWorld(position);
  }
  //#endregion

  //#region Robot 관련 메서드
  getRobots() {
    return this.robotFeature.getAll();
  }

  addRobot(robotId, element, position) {
    this.robotFeature.add(robotId, element, position);
  }

  removeRobot(robotId) {
    this.robotFeature.remove(robotId);
  }
  //#endregion

  //#region Home 관련 메서드
  addHomeMarker(robotId, position, color) {
    this.homeFeature.addMarker(robotId, position, color);
  }

  addHomePath(robotId, position) {
    this.homeFeature.addPath(robotId, position);
  }

  removeHome(robotId) {
    this.homeFeature.remove(robotId);
  }
  //#endregion

  //#region Pilot 관련 메서드
  addPilotMarker(robotId, position, color) {
    this.pilotFeature.addMarker(robotId, position, color);
  }

  removePilotMarker(robotId) {
    this.pilotFeature.removeMarker(robotId);
  }
  //#endregion

  //#region Dock 관련 메서드
  addDockMarker(robotId, position, color) {
    this.dockFeature.addMarker(robotId, position, color);
  }

  removeDockMarker(robotId) {
    this.dockFeature.removeMarker(robotId);
  }
  //#endregion

  //#region Goto 관련 메서드
  addGotoMarker(robotId, position, color) {
    this.gotoFeature.addMarker(robotId, position, color);
  }

  addGotoPath(robotId, position, color) {
    this.gotoFeature.addPath(robotId, position, color);
  }

  removeGoto(robotId) {
    this.gotoFeature.remove(robotId);
  }
  //#endregion

  //#region Way 관련 메서드
  addWays(robotId, markers, color) {
    return this.wayFeature.add(robotId, markers, color);
  }

  removeWays(robotId) {
    this.wayFeature.remove(robotId);
  }
  //#endregion

  //#region Fence 관련 메서드
  addFences(robotId, fences, color) {
    this.fenceFeature.add(robotId, fences, color);
  }

  removeFences(robotId) {
    this.fenceFeature.remove(robotId);
  }
  //#endregion

  //#region Shoot 관련 메서드
  addShoot(robotId, position) {
    this.shootFeature.add(robotId, position);
  }

  removeShoot(robotId) {
    this.shootFeature.remove(robotId);
  }
  //#endregion

  //#region Trace 관련 메서드
  addTrace(robotId, position, traceType) {
    this.traceFeature.add(robotId, position, traceType);
  }

  removeTrace(robotId) {
    this.traceFeature.remove(robotId);
  }

  setTraceType(robotId, traceType) {
    this.traceFeature.setType(robotId, traceType);
  }

  setTraceVisible(robotId, visible) {
    this.traceFeature.setVisible(robotId, visible);
  }
  //#endregion

  //#region Photo 관련 메서드
  addPhoto(robotId, imageId, element, position, url, aglAltitude, focalLength, sensorSize, visible, type) {
    this.photoFeature.add(
      robotId,
      imageId,
      element,
      position,
      url,
      aglAltitude,
      focalLength,
      sensorSize,
      visible,
      type
    );
  }

  setPhotosVisible(robotId, visible) {
    this.photoFeature.setVisible(robotId, visible);
  }

  setPhotoType(robotId, type) {
    this.photoFeature.setType(robotId, type);
  }

  resizePhotos(robotId, focalLength, sensorSize) {
    this.photoFeature.resize(robotId, focalLength, sensorSize);
  }

  removePhotos(robotId) {
    this.photoFeature.remove(robotId);
  }
  //#endregion
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new OlMap();
